// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The merge plan: the exact set of changes a merge will apply, after closure.
 *
 * A text diff can hand you hunk 3 without hunk 4 and the worst that happens is
 * the code will not compile. A board file has a harder constraint: objects
 * reference each other by UUID, and a reference to something that was not taken
 * is a file KiCad may refuse to load. So a selection is not yet a plan — the plan
 * is what the selection *entails*.
 *
 * Two rules, and they are deliberately asymmetric:
 *
 *  - Taking a container (a `group`, or a `generated` length-tuning pattern)
 *    requires taking its members. A container whose `members` name UUIDs that are
 *    not in the output is exactly the dangling reference the old V9 check
 *    refused to produce. Here it is repaired rather than refused: the members are
 *    pulled in, and the user is told.
 *
 *  - Taking a member does *not* require taking its container. The object simply
 *    arrives without its grouping. That is a loss, not a corruption, so it is
 *    reported and allowed.
 */

import type { DiffResult, ObjectChange } from '../diff/diff.ts';
import type { BoardObject } from '../model/board.ts';
import { groupMembers, MEMBER_CONTAINER_TYPES } from '../model/objects.ts';
import type { Selection } from './selection.ts';
import { takenChanges } from './selection.ts';

/**
 * Board-level blocks that belong to no position on the canvas, and so can never
 * be caught by a rectangle. They are selected from a list instead.
 */
export type GlobalBlockId = 'layers' | 'setup' | 'properties' | 'title_block' | 'nets';

export const GLOBAL_BLOCK_INFO: Record<GlobalBlockId, { label: string; help: string }> = {
  layers: {
    label: 'Layer stack',
    help:
      'The board’s layer table. Taking Board B’s stack changes what the board physically is — every ' +
      'object in Board A that draws on a layer B does not have would be left referencing a layer that ' +
      'no longer exists.',
  },
  setup: {
    label: 'Design rules',
    help:
      'Clearances, track widths, via sizes, netclasses and the stackup. These govern the whole board, so ' +
      'taking Board B’s replaces Board A’s outright rather than merging them rule by rule.',
  },
  properties: {
    label: 'Text variables',
    help:
      'Board text variables. Any text drawn from a variable changes meaning when the variable does, so ' +
      'silkscreen and fabrication notes can read differently without any object having changed.',
  },
  title_block: {
    label: 'Title block',
    help: 'Sheet title, date, revision and company. Cosmetic, but it is what a fabricator reads first.',
  },
  nets: {
    label: 'Missing nets',
    help:
      'Adds the nets your selection needs to Board A’s net table, so that copper arriving from Board B ' +
      'has something to attach to. Board A’s existing nets and their indices are untouched — replacing ' +
      'the table wholesale would renumber every net and silently reattach every object in Board A.',
  },
};

export interface MergePlan {
  /** Object changes to apply, by {@link ObjectChange.key}. */
  take: ReadonlySet<string>;
  /** Board-level blocks to take from Board B. */
  takeGlobal: ReadonlySet<GlobalBlockId>;
}

export type PlanNoteKind = 'pulled-in' | 'ungrouped';

export interface PlanNote {
  kind: PlanNoteKind;
  /** One sentence, already written for a person rather than a log. */
  message: string;
  /** Change keys the note is about, so the UI can select them. */
  changeKeys: string[];
}

export interface BuiltPlan {
  plan: MergePlan;
  /** Everything closure did on the user's behalf, so none of it is silent. */
  notes: PlanNote[];
  /** Changes taken because the user pointed at them. */
  chosen: ObjectChange[];
  /** Changes taken because something else required them. */
  pulledIn: ObjectChange[];
}

/**
 * Turn selections into a plan, closing over referential dependencies.
 *
 * Runs to a fixed point: pulling in a group's members can pull in another group,
 * because KiCad allows a group to contain a group.
 */
export function buildPlan(
  diff: DiffResult,
  selections: readonly Selection[],
  globals: readonly GlobalBlockId[] = [],
): BuiltPlan {
  const chosen = takenChanges(diff, selections);
  const take = new Set(chosen.map((c) => c.key));

  const byUuid = indexByUuid(diff);
  const containerOf = buildContainerIndex(diff);

  const pulledIn: ObjectChange[] = [];
  const notes: PlanNote[] = [];

  // --- rule 1: a container brings its members ---------------------------------
  const queue = [...chosen];
  const pulledForContainer = new Map<string, string[]>();

  while (queue.length > 0) {
    const change = queue.pop()!;
    const side = change.b ?? change.a;
    if (side === undefined || !MEMBER_CONTAINER_TYPES.has(side.type)) continue;

    for (const memberUuid of groupMembers(side.node)) {
      const memberChange = byUuid.get(memberUuid);
      if (memberChange === undefined || take.has(memberChange.key)) continue;
      take.add(memberChange.key);
      pulledIn.push(memberChange);
      queue.push(memberChange);

      const label = describe(side);
      const list = pulledForContainer.get(label);
      if (list) list.push(memberChange.key);
      else pulledForContainer.set(label, [memberChange.key]);
    }
  }

  for (const [container, keys] of pulledForContainer) {
    notes.push({
      kind: 'pulled-in',
      message:
        `Also taking ${keys.length} change(s) that belong to ${container}. Taking a group without its ` +
        `members would leave it pointing at objects that are not in the file.`,
      changeKeys: keys,
    });
  }

  // --- rule 2: a member without its container loses its grouping ---------------
  // Indexed rather than searched: a real revision can carry tens of thousands of
  // changes, and a linear lookup per taken change would be quadratic.
  const byKey = new Map(diff.substantive.map((c) => [c.key, c]));
  const ungrouped = new Map<string, string[]>();
  for (const key of take) {
    const change = byKey.get(key);
    const side = change?.b ?? change?.a;
    if (side === undefined) continue;
    const container = side.uuid === undefined ? undefined : containerOf.get(side.uuid);
    if (container === undefined) continue;
    if (container.change !== undefined && take.has(container.change.key)) continue;
    const list = ungrouped.get(container.label);
    if (list) list.push(key);
    else ungrouped.set(container.label, [key]);
  }

  for (const [container, keys] of ungrouped) {
    notes.push({
      kind: 'ungrouped',
      message:
        `${keys.length} change(s) belong to ${container}, which is not being taken. They will arrive ` +
        `without their grouping — nothing is broken, but the group will no longer contain them.`,
      changeKeys: keys,
    });
  }

  return {
    plan: { take, takeGlobal: new Set(globals) },
    notes,
    chosen,
    pulledIn,
  };
}

/** Every change, keyed by the UUID of either side. */
function indexByUuid(diff: DiffResult): Map<string, ObjectChange> {
  const out = new Map<string, ObjectChange>();
  for (const change of diff.substantive) {
    for (const side of [change.b, change.a]) {
      if (side?.uuid !== undefined && side.uuid !== '') out.set(side.uuid, change);
    }
  }
  return out;
}

interface ContainerRef {
  label: string;
  /** The container's own change, if it has one. Absent when it did not change. */
  change?: ObjectChange;
}

/**
 * Which container claims each member UUID.
 *
 * Built from both boards: a member may belong to a group that itself did not
 * change, and that group still loses a member if the member is spliced.
 */
function buildContainerIndex(diff: DiffResult): Map<string, ContainerRef> {
  const out = new Map<string, ContainerRef>();
  const changeOf = new Map<string, ObjectChange>();
  for (const change of diff.substantive) {
    for (const side of [change.b, change.a]) {
      if (side?.uuid !== undefined && side.uuid !== '') changeOf.set(side.uuid, change);
    }
  }

  for (const board of [diff.a, diff.b]) {
    for (const object of board.objects) {
      if (!MEMBER_CONTAINER_TYPES.has(object.type)) continue;
      const ref: ContainerRef = { label: describe(object) };
      const own = object.uuid === undefined ? undefined : changeOf.get(object.uuid);
      if (own !== undefined) ref.change = own;
      for (const member of groupMembers(object.node)) {
        if (!out.has(member)) out.set(member, ref);
      }
    }
  }
  return out;
}

function describe(o: BoardObject): string {
  const name = o.type === 'group' ? quotedName(o) : undefined;
  if (name !== undefined) return `group ${name}`;
  return o.type === 'generated' ? 'a length-tuning pattern' : `a ${o.type}`;
}

function quotedName(o: BoardObject): string | undefined {
  for (const child of o.node.children) {
    if (child.kind === 'atom' && child.quoted) return `"${child.value}"`;
  }
  return undefined;
}
